/**
 * Custom storage provider for Supabase auth that handles different hosting environments
 * Fixes issues with localStorage on certain hosting platforms like Hostinger
 */

type StorageValue = string | null;

interface IStorage {
  getItem(key: string): StorageValue;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Hybrid storage that tries localStorage first, then falls back to in-memory storage
 * Useful for hosting environments that have localStorage restrictions
 */
export class HybridAuthStorage implements IStorage {
  private memoryStorage = new Map<string, string>();
  private isLocalStorageAvailable = false;

  constructor() {
    this.checkLocalStorageAvailability();
  }

  private checkLocalStorageAvailability() {
    try {
      const testKey = '__edulive_auth_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      this.isLocalStorageAvailable = true;
      console.log('[AuthStorage] localStorage is available');
    } catch (error) {
      console.warn('[AuthStorage] localStorage is not available, using memory storage:', error);
      this.isLocalStorageAvailable = false;
    }
  }

  getItem(key: string): StorageValue {
    try {
      if (this.isLocalStorageAvailable) {
        const value = window.localStorage.getItem(key);
        if (value) {
          console.log(`[AuthStorage] Retrieved ${key} from localStorage`);
          return value;
        }
      }
    } catch (error) {
      console.warn(`[AuthStorage] Error reading from localStorage: ${key}`, error);
    }
    
    const value = this.memoryStorage.get(key);
    if (value) {
      console.log(`[AuthStorage] Retrieved ${key} from memory`);
    }
    return value || null;
  }

  setItem(key: string, value: string): void {
    console.log(`[AuthStorage] Storing ${key} (length: ${value.length})`);
    
    try {
      if (this.isLocalStorageAvailable) {
        window.localStorage.setItem(key, value);
        console.log(`[AuthStorage] Stored ${key} in localStorage`);
      }
    } catch (error) {
      console.warn(`[AuthStorage] Error writing to localStorage: ${key}`, error);
    }
    
    this.memoryStorage.set(key, value);
    console.log(`[AuthStorage] Stored ${key} in memory`);
  }

  removeItem(key: string): void {
    console.log(`[AuthStorage] Removing ${key}`);
    try {
      if (this.isLocalStorageAvailable) {
        window.localStorage.removeItem(key);
      }
    } catch (error) {
      console.warn(`[AuthStorage] Error removing from localStorage: ${key}`, error);
    }
    
    this.memoryStorage.delete(key);
  }
}
